import { useFlowSessionById } from "@/api/flow_sessions";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFlowSessionActions } from "@/hooks/useFlowSessionActions";
import { ChevronDown, Pencil, Trash } from "lucide-react";
import React, { useState } from "react";

import {
  defaultAssistantIcon,
  defaultChatIcon,
  IconWithBackground,
} from "@cohesive/ui-components";

import { RenameDialog } from "@/components/Dialogs/RenameDialog";

import { flowSessionModeIcon } from "@/constants/flow-session-modes";
import { cn } from "@/utils";
import Typewriter from "../Typewriter/Typewriter";

interface SessionNameMenuProps {
  sessionId: string;
  className?: string;
  style?: React.CSSProperties;
  onDelete?: () => void;
  onRename?: () => void;
}

const SessionNameMenu: React.FC<SessionNameMenuProps> = ({
  sessionId,
  className,
  style,
  onDelete,
  onRename,
}: SessionNameMenuProps) => {
  const { data: flowSession } = useFlowSessionById(sessionId ?? "");
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const { handleRename, handleDelete } = useFlowSessionActions({
    onDelete,
    onRename,
  });

  const icon = flowSessionModeIcon(flowSession?.mode) ?? defaultChatIcon;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex items-center gap-2 pl-1 pr-4 py-2 rounded-full mt-[22px]",
              className,
            )}
            style={style}
          >
            <IconWithBackground icon={icon || defaultAssistantIcon} size={32} />

            <span className="max-w-[150px] md:max-w-[450px] truncate transition-all duration-300">
              {flowSession?.name ? (
                <Typewriter
                  text={flowSession.name}
                  enabled={!!flowSession?.typewrite}
                  speed={25}
                />
              ) : (
                "New Chat"
              )}
            </span>
            <ChevronDown className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          <DropdownMenuItem onClick={() => setIsRenameModalOpen(true)}>
            <Pencil />
            <span>Rename</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => flowSession && handleDelete(flowSession)}
            className="text-lc-blocked focus:text-lc-blocked dark:text-lc-blocked dark:focus:text-lc-blocked"
          >
            <Trash />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameDialog
        open={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
        initialValues={{
          name: flowSession?.name || "New Chat",
        }}
        onSubmit={(values) => {
          if (flowSession) {
            handleRename(flowSession, values.name);
            setIsRenameModalOpen(false);
          }
        }}
      />
    </>
  );
};

export default SessionNameMenu;
